/**
 * The start/stop wiring behind `TaskRow`'s timer slot (V6-06).
 *
 * My Day and the project task list both draw the control, and both need the
 * same three things: which task the one running timer belongs to, a start that
 * reports the timer it displaced, and a stop. Keeping that here means the two
 * surfaces cannot drift into behaving differently.
 */

import { useCallback } from 'react';
import { toast } from 'sonner';
import type { TaskRowTimer } from '@/components/shared/TaskRow';
import { ApiError, api } from '@/lib/api';
import { useRunningTimer, useStopTimer } from './api';

export function useTaskRowTimer(): (taskId: string) => TaskRowTimer {
  const { data: running, refetch } = useRunningTimer();
  const stop = useStopTimer();

  const start = useCallback(
    async (taskId: string) => {
      try {
        const result = await api.post<{ stoppedPrevious?: { taskKey: string; minutes: number } }>(
          '/time/start',
          { taskId },
        );
        if (result.data.stoppedPrevious) {
          toast.info(
            `Stopped your timer on ${result.data.stoppedPrevious.taskKey} (${result.data.stoppedPrevious.minutes} min)`,
          );
        } else {
          toast.success('Timer started');
        }
        await refetch();
      } catch (err) {
        toast.error(err instanceof ApiError ? err.message : 'Could not start the timer');
      }
    },
    [refetch],
  );

  return useCallback(
    (taskId: string) => ({
      // At most one timer runs at a time, so only its own row shows elapsed.
      runningSince: running?.taskId === taskId ? running.startedAt : undefined,
      onStart: () => void start(taskId),
      onStop: () => stop.mutate(),
    }),
    [running, start, stop],
  );
}
